import React from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { AiOutlineTrophy } from 'react-icons/ai';
import { FiClipboard, FiEdit3, FiTarget, FiVideo } from 'react-icons/fi';
import { useQuery } from 'jsonapi-react';
import { IconVerified } from '@/components/custom/icon';
import { CourseCard, CourseCardBody, CourseCardContent, CourseCardHeader, CourseCardImg, CourseCardImgWrapper, CourseCardInnerHeader, CourseCardNotifications, CourseCardSubtitle, CourseCardText, CourseCardTitle } from '@/components/custom/course';
// import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CardProgressWrapper } from '@/components/ui/card';
import { NotificationItemIcon, NotificationsBadge } from '@/components/custom/notifications';
import { MedalContainer } from '@/components/custom/medal';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import courseDefaultImg from '@/assets/images/course-placeholder.jpg';

export default function DashboardCourseCard(props) {
  const navigate = useNavigate();
  const { course, journey, setShowModal, setSelectedCourse } = props;
  const { data } = useQuery(`notifications?filter[courses.id]=${course.id}`);
  //TODO: Pedir ao backend para criar um filtro que retorne todas as notificacoes de cursos em uma única chamada para evitar chamada
  //em cada render deste componente.

  const handleClick = () => {
    setSelectedCourse(course);
    setShowModal(true);
  };

  const downloadCertificate = () => {
    window.open(`${process.env.REACT_APP_DEV_URL}//user_certificates/download/${course?.certificate?.hash_id}.pdf`, '_blank');
  };

  const amountClipboardNotifications = data?.filter(item => item.icon === 'FiClipboard' && item.unread).length;
  const amountEditNotifications = data?.filter(item => item.icon === 'FiEdit3' && item.unread).length;
  const amountTargetNotifications = data?.filter(item => item.icon === 'FiTarget' && item.unread).length;
  const amountVideoNotifications = data?.filter(item => item.icon === 'FiVideo' && item.unread).length;

  const courseProgress = course?.progress;
  const playful = usePlayfulStudent();
  const goToCourse = e => {
    e?.stopPropagation?.();
    navigate(`/curso/acompanhamento/${course?.id}`);
  };

  // Card lúdico (estilo trilha): imagem no topo, conteúdo abaixo, botão chanfrado.
  if (playful) {
    const done = course?.progress === 100;
    return (
      <CourseCard
        onClick={goToCourse}
        className="group relative flex !flex-col !items-stretch !gap-0 overflow-hidden rounded-[24px] border border-neutral-lighter bg-white !p-0 shadow-card transition-[transform,box-shadow] duration-150 ease-out-strong hover:-translate-y-0.5 hover:shadow-card-hover"
      >
        <CourseCardImg
          src={course?.image?.url || courseDefaultImg}
          alt=""
          className="!h-[130px] !w-full !rounded-none object-cover"
        />
        <div className="flex flex-1 flex-col gap-3 p-6">
          <div className="flex-1">
            <h3
              className="mb-0 line-clamp-2 text-base font-extrabold leading-tight"
              style={{ color: '#222731' }}
            >
              {course?.title}
            </h3>
            <p className="mb-0 mt-1 text-xs font-semibold text-neutral-500">
              {course?.['finished-learning-system-items']}/{course?.['learning-system-item-count']} {t('dashboard.completedLessons')}
            </p>
          </div>
          <CardProgressWrapper
            percentage={courseProgress}
            toFixed={0}
          />
          <button
            type="button"
            onClick={goToCourse}
            className="group/btn relative w-full overflow-hidden rounded-full py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
            style={{
              background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-secondary) 88%, #fff) 0%, var(--color-secondary) 100%)',
              boxShadow: '0 4px 0 color-mix(in srgb, var(--color-secondary) 62%, #000), 0 6px 14px rgba(0,0,0,0.16)'
            }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
            />
            <span className="relative">{done ? 'Rever curso' : courseProgress > 0 ? 'Continuar' : 'Acessar curso'}</span>
          </button>
        </div>
      </CourseCard>
    );
  }

  return (
    <CourseCard
      onClick={() => {
        navigate(window.location.pathname === '/curso' || window.location.pathname === '/' ? `/curso/acompanhamento/${course?.id}` : handleClick());
      }}
    >
      <CourseCardBody alignment="home">
        <CourseCardImgWrapper>
          {/* <Badge size="xs">{course?.knowledge ? course?.knowledge : ''}</Badge> */}
          <CourseCardImg
            size="md"
            src={course?.image?.url}
            alt=""
          />
        </CourseCardImgWrapper>

        <CourseCardContent>
          <CourseCardHeader>
            <CourseCardInnerHeader>
              <div className="flex items-start gap-1.5">
                {course?.['official-content'] && <IconVerified />}
                <CourseCardTitle>{course?.title}</CourseCardTitle>
              </div>
              <CourseCardSubtitle>
                {course?.['finished-learning-system-items']}/{course?.['learning-system-item-count']} {t('dashboard.completedLessons')}
              </CourseCardSubtitle>
            </CourseCardInnerHeader>

            <CourseCardNotifications>
              {amountClipboardNotifications > 0 && (
                <NotificationItemIcon
                  variant="warning"
                  size="sm"
                >
                  <FiClipboard />
                  <NotificationsBadge>{amountClipboardNotifications > 9 ? '+9' : amountClipboardNotifications}</NotificationsBadge>
                </NotificationItemIcon>
              )}

              {amountEditNotifications > 0 && (
                <NotificationItemIcon
                  variant="warning"
                  size="sm"
                >
                  <FiEdit3 />
                  <NotificationsBadge>{amountEditNotifications > 9 ? '+9' : amountEditNotifications}</NotificationsBadge>
                </NotificationItemIcon>
              )}

              {amountTargetNotifications > 0 && (
                <NotificationItemIcon
                  variant="warning"
                  size="sm"
                >
                  <FiTarget />
                  <NotificationsBadge>{amountTargetNotifications > 9 ? '+9' : amountTargetNotifications}</NotificationsBadge>
                </NotificationItemIcon>
              )}

              {amountVideoNotifications > 0 && (
                <NotificationItemIcon
                  variant="warning"
                  size="sm"
                >
                  <FiVideo />
                  <NotificationsBadge>{amountVideoNotifications > 9 ? '+9' : amountVideoNotifications}</NotificationsBadge>
                </NotificationItemIcon>
              )}
            </CourseCardNotifications>

            {course?.progress === 100 && (
              <MedalContainer
                size="sm"
                src="more"
                value={<AiOutlineTrophy />}
              />
            )}
          </CourseCardHeader>

          <div>
            {course?.progress === 100 && !journey ? (
              <>
                <Badge
                  variant="success"
                  size="xs"
                >
                  Concluído
                </Badge>

                {/*{course?.certificate?.hash_id && (*/}
                {/*  <div>*/}
                {/*    <Button*/}
                {/*      variant="link"*/}
                {/*      className="px-0"*/}
                {/*      asChild*/}
                {/*    >*/}
                {/*      <a*/}
                {/*        onClick={e => {*/}
                {/*          e.stopPropagation();*/}
                {/*          downloadCertificate();*/}
                {/*        }}*/}
                {/*      >*/}
                {/*        {t('button.certification')}*/}
                {/*      </a>*/}
                {/*    </Button>*/}
                {/*  </div>*/}
                {/*)}*/}
              </>
            ) : (
              <CardProgressWrapper
                percentage={courseProgress}
                toFixed={0}
              />
            )}
          </div>
        </CourseCardContent>
      </CourseCardBody>
    </CourseCard>
  );
}
